import { SeletorFechamento, PreviaFechamento } from './FechamentoCartao'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { cartoesApi, contasApi, categoriasApi, transacoesApi } from '../../api/endpoints'
import { Button, IconButton } from '../../components/ui/Button'
import { Input, Select } from '../../components/ui/Input'
import { Card, CardHeader, CardBody, AvatarIcon, ProgressBar } from '../../components/ui/Card'
import { Badge, DonoBadge } from '../../components/ui/Badge'
import { LoadingState, EmptyState } from '../../components/ui/EmptyState'
import { Modal, ConfirmDialog } from '../../components/ui/Modal'
import { MonthNavigator } from '../../components/ui/Navigation'
import { Table, THead, Th, ThOrdenavel, Tr, Td } from '../../components/ui/Table'
import { useToast } from '../../components/ui/Toast'
import { PageHeader } from '../../layout/PageHeader'
import { AccountType, ModalidadeCartao, MODALIDADE_CARTAO_LABEL, StatusFatura, STATUS_FATURA_LABEL, EscopoParcelas } from '../../lib/enums'
import { formatMoney, formatDate, cn } from '../../lib/utils'
import { useOrdenarBuscarLocal } from '../../lib/ordenarLancamentos'
import type { CreditCard, FaturaTransacao, PagamentoJaImportado } from '../../types'
import { PainelContaCartao } from './PainelContaCartao'
import { BotaoFundirLancamento } from '../transacoes/FundirLancamento'
import { BotaoAjusteFaturaAtalho } from './AjusteFaturaAtalho'
import { AjusteFaturaAcoes } from './AjusteFaturaAcoes'
import { BotaoImportarCsv } from './ImportarCsv'
import { FaturaCategoriasPanel } from './FaturaCategorias'

const NOMES_MES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro']

const STATUS_VARIANT: Record<StatusFatura, 'warning' | 'info' | 'income'> = {
  [StatusFatura.Aberta]: 'warning',
  [StatusFatura.Fechada]: 'info',
  [StatusFatura.Paga]: 'income',
}

const EMPTY_CARD = { modalidade: ModalidadeCartao.Credito as ModalidadeCartao, nome: '', limite: '', diaFechamento: '25', diaVencimento: '5', accountId: '', saldoInicial: '', temDebito: true, compraNoFechamento: false }

const ESCOPOS_FATURA: { valor: EscopoParcelas; rotulo: string; ajuda: string }[] = [
  { valor: EscopoParcelas.Apenas, rotulo: 'Só esta parcela', ajuda: 'As outras continuam como estão.' },
  { valor: EscopoParcelas.Proximas, rotulo: 'Esta e as próximas', ajuda: 'Bom para cancelar ou ajustar o resto do parcelamento.' },
  { valor: EscopoParcelas.Todas, rotulo: 'Todas as parcelas', ajuda: 'Parcelas de faturas já pagas ficam como estão.' },
]

const MODALIDADES: { valor: ModalidadeCartao; titulo: string; ajuda: string }[] = [
  { valor: ModalidadeCartao.Credito, titulo: 'Crédito', ajuda: 'Tem limite e fatura. A compra só sai da conta quando você paga a fatura.' },
  { valor: ModalidadeCartao.Debito, titulo: 'Débito', ajuda: 'A compra sai da conta na hora, à vista.' },
  { valor: ModalidadeCartao.PrePago, titulo: 'Pré-pago', ajuda: 'Tem saldo próprio, como o vale-alimentação. Você recarrega e as compras saem dele.' },
]

function emptyCompra() {
  return { description: '', amount: '', date: new Date().toISOString().slice(0, 10), numParcelas: '1', categoriaId: '', notes: '', debito: false }
}

/** "Também é cartão de débito": a compra no débito sai da conta que paga a fatura. */
function CaixaDebito({ marcado, onChange }: { marcado: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex cursor-pointer items-start gap-2 sm:col-span-2">
      <input type="checkbox" checked={marcado} onChange={(e) => onChange(e.target.checked)} className="mt-0.5 h-4 w-4 rounded accent-primary" />
      <span>
        <span className="block text-13 font-medium text-white">Também é cartão de débito</span>
        <span className="block text-13 text-muted">No lançamento você escolhe Crédito ou Débito. O débito sai da conta que paga a fatura, à vista.</span>
      </span>
    </label>
  )
}

export function CartaoPage() {
  const qc = useQueryClient()
  const toast = useToast()
  const now = new Date()
  // Vindo de "Pagar" na tela de Transações: já abre o cartão e o mês da fatura.
  const [busca, setBusca] = useSearchParams()
  const [selectedId, setSelectedId] = useState<string | null>(busca.get('cartao'))
  const [mes, setMes] = useState(Number(busca.get('mes')) || now.getMonth() + 1)
  const [ano, setAno] = useState(Number(busca.get('ano')) || now.getFullYear())
  const [dataPagamento, setDataPagamento] = useState('')
  const [semDebito, setSemDebito] = useState(false)
  const [cardModal, setCardModal] = useState(false)
  const [compraModal, setCompraModal] = useState(false)
  const [confirmPagar, setConfirmPagar] = useState(false)
  const [visaoFatura, setVisaoFatura] = useState<'compras' | 'categorias'>('compras')
  // Pagamento da fatura que já veio da Pluggy como débito na conta: por padrão vincula (paga sem debitar de novo).
  const [vincularImportado, setVincularImportado] = useState(true)
  const [editando, setEditando] = useState<{ id: string; compraId: string | null; parcelaTotalNumero?: number; ehDaPluggy?: boolean; faturaPaga?: boolean } | null>(null)
  const [movendo, setMovendo] = useState<FaturaTransacao | null>(null)
  const [faturaDestinoId, setFaturaDestinoId] = useState('')
  const [reabrindo, setReabrindo] = useState(false)
  const [cardForm, setCardForm] = useState(EMPTY_CARD)
  const [editandoCartao, setEditandoCartao] = useState(false)
  const [compra, setCompra] = useState(emptyCompra())
  const [erro, setErro] = useState<string | null>(null)
  const [mostrarNovaCategoria, setMostrarNovaCategoria] = useState(false)
  const [novaCategoria, setNovaCategoria] = useState('')

  const { data: cartoes = [], isLoading } = useQuery({ queryKey: ['cartoes'], queryFn: cartoesApi.list })
  const { data: todasContas = [] } = useQuery({ queryKey: ['contas'], queryFn: () => contasApi.list(), enabled: cardModal })
  // A conta Benefício é o saldo de um cartão pré-pago: não serve para pagar fatura nem para o débito.
  const contas = todasContas.filter((c) => c.type !== AccountType.Benefit)
  const { data: categorias = [] } = useQuery({ queryKey: ['categorias'], queryFn: () => categoriasApi.list({ incluirInativas: false }), enabled: compraModal || !!editando })

  const selected: CreditCard | undefined = cartoes.find((c) => c.id === selectedId) ?? cartoes[0]
  const credito = !selected || selected.modalidade === ModalidadeCartao.Credito
  const comDebito = !!selected && credito && selected.temDebito
  // Compra à vista (sai da conta na hora): cartão de débito, pré-pago, ou crédito lançado como débito.
  const compraAVista = !credito || (comDebito && compra.debito)
  const [aba, setAba] = useState<'fatura' | 'debito'>('fatura')
  const abaAtiva = comDebito ? aba : 'fatura'

  const { data: fatura, isLoading: loadingFatura } = useQuery({
    queryKey: ['fatura', selected?.id, mes, ano],
    queryFn: () => cartoesApi.getFatura(selected!.id, mes, ano),
    enabled: !!selected && selected.modalidade === ModalidadeCartao.Credito,
  })
  const ordenacaoFatura = useOrdenarBuscarLocal(fatura?.transacoes ?? [], {
    nome: (t) => t.description, data: (t) => t.date, valor: (t) => t.amount, parcela: (t) => t.parcelaNumero,
  })

  const criarCartao = useMutation({
    mutationFn: () => {
      const nome = cardForm.nome.trim()
      if (cardForm.modalidade === ModalidadeCartao.PrePago)
        return cartoesApi.criar({ modalidade: cardForm.modalidade, nome, saldoInicial: cardForm.saldoInicial ? Number(cardForm.saldoInicial) : undefined })
      if (cardForm.modalidade === ModalidadeCartao.Debito)
        return cartoesApi.criar({ modalidade: cardForm.modalidade, nome, accountId: cardForm.accountId })
      return cartoesApi.criar({
        modalidade: cardForm.modalidade, nome, accountId: cardForm.accountId,
        limite: Number(cardForm.limite), diaFechamento: Number(cardForm.diaFechamento), diaVencimento: Number(cardForm.diaVencimento),
        temDebito: cardForm.temDebito,
        compraNoFechamentoNaFaturaQueFecha: cardForm.compraNoFechamento,
      })
    },
    onSuccess: (c) => {
      qc.invalidateQueries({ queryKey: ['cartoes'] })
      qc.invalidateQueries({ queryKey: ['onboarding'] })
      qc.invalidateQueries({ queryKey: ['contas'] })
      setSelectedId(c.id)
      setCardModal(false)
      toast.success(c.modalidade === ModalidadeCartao.PrePago ? `Cartão pré-pago criado. O saldo dele está na conta "${c.accountName}".` : 'Cartão criado.')
    },
    onError: (e: Error) => setErro(e.message),
  })

  const adicionarCompra = useMutation({
    mutationFn: () => cartoesApi.adicionarTransacao(selected!.id, {
      description: compra.description,
      amount: Number(compra.amount),
      date: compra.date,
      numParcelas: compraAVista ? 1 : Number(compra.numParcelas) || 1,
      debito: comDebito && compra.debito ? true : undefined,
      categoriaId: compra.categoriaId || undefined,
      notes: compra.notes || undefined,
    }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['fatura'] })
      qc.invalidateQueries({ queryKey: ['cartoes'] })
      qc.invalidateQueries({ queryKey: ['transacoes'] })
      qc.invalidateQueries({ queryKey: ['contas'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
      // Mostra a fatura em que a (primeira) parcela caiu, como o servidor decidiu.
      const primeira = r.parcelas[0]
      if (primeira) { setMes(primeira.faturaMes); setAno(primeira.faturaAno) }
      setCompraModal(false)
      if (compraAVista) setAba(comDebito ? 'debito' : 'fatura')
      toast.success(compraAVista ? 'Compra registrada.' : Number(compra.numParcelas) > 1 ? `Compra lançada em ${compra.numParcelas} parcelas.` : 'Compra lançada.')
    },
    onError: (e: Error) => setErro(e.message),
  })

  const criarCategoria = useMutation({
    mutationFn: () => categoriasApi.criar({ nome: novaCategoria.trim() }),
    onSuccess: (categoria) => {
      qc.invalidateQueries({ queryKey: ['categorias'] })
      setCompra((f) => ({ ...f, categoriaId: categoria.id }))
      setNovaCategoria('')
      setMostrarNovaCategoria(false)
      toast.success('Categoria criada e selecionada.')
    },
    onError: (e: Error) => setErro(e.message),
  })

  const pagarFatura = useMutation({
    mutationFn: ({ faturaId, data, semDebito, vincularTransacaoId }: { faturaId: string; data: string; semDebito: boolean; vincularTransacaoId?: string }) =>
      cartoesApi.pagarFatura(faturaId, new Date(`${data}T12:00:00Z`).toISOString(), semDebito, vincularTransacaoId),
    onSuccess: (r, v) => {
      qc.invalidateQueries({ queryKey: ['fatura'] })
      qc.invalidateQueries({ queryKey: ['cartoes'] })
      qc.invalidateQueries({ queryKey: ['contas'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
      qc.invalidateQueries({ queryKey: ['transacoes'] })
      qc.invalidateQueries({ queryKey: ['faturas-resumo'] })
      setConfirmPagar(false)
      qc.invalidateQueries({ queryKey: ['fatura-categorias'] })
      if (v.vincularTransacaoId) toast.success('Fatura paga e vinculada ao pagamento que veio da Pluggy. O saldo da conta não foi debitado de novo.')
      else if (v.semDebito) toast.success('Fatura marcada como paga. O saldo da conta não foi alterado.')
      else if (r?.pagamentoJaImportado) toast.error(`Fatura paga e debitada, mas já existe um débito de ${formatMoney(r.pagamentoJaImportado.valor)} em ${formatDate(r.pagamentoJaImportado.data)} (${r.pagamentoJaImportado.descricao}) vindo da Pluggy: o pagamento pode ter sido contado em dobro. Reabra a fatura e pague de novo vinculando, se for o mesmo.`)
      else toast.success('Fatura paga e debitada da conta vinculada na data informada.')
    },
    onError: (e: Error) => toast.error(e.message),
  })

  // ── Editar/excluir direto na fatura ─────────────────────────────────────────
  const [editForm, setEditForm] = useState({ description: '', amount: '', categoriaId: '', date: '', notes: '' })
  const [erroEdicao, setErroEdicao] = useState<string | null>(null)
  const [paraExcluir, setParaExcluir] = useState<FaturaTransacao | null>(null)
  const [escopoSalvar, setEscopoSalvar] = useState(false)
  const [escopoEscolhido, setEscopoEscolhido] = useState<EscopoParcelas>(EscopoParcelas.Apenas)

  const invalidarFatura = () => {
    qc.invalidateQueries({ queryKey: ['fatura'] })
    qc.invalidateQueries({ queryKey: ['cartoes'] })
    qc.invalidateQueries({ queryKey: ['transacoes'] })
    qc.invalidateQueries({ queryKey: ['contas'] })
    qc.invalidateQueries({ queryKey: ['dashboard'] })
  }

  async function abrirEdicao(t: FaturaTransacao) {
    const completo = await transacoesApi.get(t.id)
    setErroEdicao(null)
    setEditando({ id: t.id, compraId: completo.compraId ?? null, parcelaTotalNumero: t.parcelaTotalNumero, ehDaPluggy: completo.ehDaPluggy, faturaPaga: completo.faturaPaga })
    setEditForm({
      description: completo.description, amount: String(Math.abs(completo.amount)),
      categoriaId: completo.categoriaId ?? '', date: completo.date.slice(0, 10), notes: completo.notes ?? '',
    })
  }

  const salvarEdicao = useMutation({
    mutationFn: async (escopo: EscopoParcelas) => {
      const payload = {
        description: editForm.description.trim(), amount: Number(editForm.amount),
        categoriaId: editForm.categoriaId || undefined, date: editForm.date, notes: editForm.notes || undefined,
      }
      if (editando?.faturaPaga) {
        // Fatura paga: só a categoria (e descrição/observação, se veio da Pluggy).
        return transacoesApi.atualizar(editando.id, {
          categoriaId: payload.categoriaId,
          ...(editando.ehDaPluggy ? { description: payload.description, notes: payload.notes } : {}),
        }, escopo)
      }
      try {
        return await transacoesApi.atualizar(editando!.id, payload, escopo)
      } catch (e) {
        // Fatura já paga: só descrição/categoria/observação de um lançamento da Pluggy podem mudar depois.
        if (editando?.ehDaPluggy) {
          return await transacoesApi.atualizar(editando!.id, {
            description: payload.description, categoriaId: payload.categoriaId, notes: payload.notes,
          }, escopo)
        }
        throw e
      }
    },
    onSuccess: () => { invalidarFatura(); setEditando(null); setEscopoSalvar(false); toast.success('Lançamento atualizado.') },
    onError: (e: Error) => setErroEdicao(e.message),
  })

  function confirmarEdicao() {
    if (!editForm.description.trim() || !editForm.amount) { setErroEdicao('Preencha descrição e valor.'); return }
    if (editando && editando.compraId && (editando.parcelaTotalNumero ?? 0) > 1) {
      setEscopoEscolhido(EscopoParcelas.Apenas)
      setEscopoSalvar(true)
      return
    }
    salvarEdicao.mutate(EscopoParcelas.Apenas)
  }

  const remover = useMutation({
    mutationFn: ({ id, escopo }: { id: string; escopo: EscopoParcelas }) => transacoesApi.remover(id, escopo),
    onSuccess: () => { invalidarFatura(); setParaExcluir(null); toast.success('Lançamento excluído.') },
    onError: (e: Error) => toast.error(e.message),
  })

  const { data: faturasDoCartao = [] } = useQuery({
    queryKey: ['faturas', selected?.id],
    queryFn: () => cartoesApi.listFaturas(selected!.id),
    enabled: !!movendo,
  })
  const mover = useMutation({
    mutationFn: () => cartoesApi.moverParaFatura(movendo!.id, faturaDestinoId),
    onSuccess: () => { invalidarFatura(); toast.success('Lançamento movido pra outra fatura.'); setMovendo(null); setFaturaDestinoId('') },
    onError: (e: Error) => toast.error(e.message),
  })
  const reabrir = useMutation({
    mutationFn: () => cartoesApi.reabrirFatura(fatura!.id),
    onSuccess: () => { invalidarFatura(); toast.success('Fatura reaberta.'); setReabrindo(false) },
    onError: (e: Error) => toast.error(e.message),
  })

  // Ao abrir o diálogo (e ao mudar a data), procura um débito de pagamento de fatura já importado da conta.
  const { data: pagamentoImportado } = useQuery<PagamentoJaImportado | null>({
    queryKey: ['pagamento-importado', fatura?.id, dataPagamento],
    queryFn: () => cartoesApi.pagamentoImportado(fatura!.id, dataPagamento ? new Date(`${dataPagamento}T12:00:00Z`).toISOString() : undefined),
    enabled: confirmPagar && !!fatura && !!dataPagamento,
    retry: false,
  })
  const vinculando = !!pagamentoImportado && vincularImportado

  function abrirPagamento() {
    // Sugere o vencimento; dá para adiantar ou atrasar informando outra data.
    setDataPagamento((fatura?.dataVencimento ?? new Date().toISOString()).slice(0, 10))
    setSemDebito(false)
    setVincularImportado(true)
    setConfirmPagar(true)
  }

  useEffect(() => {
    if (busca.get('pagar') === '1' && fatura && fatura.status !== StatusFatura.Paga && fatura.valorTotal > 0) {
      abrirPagamento()
      setBusca({}, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fatura])

  const fechamentoAtual = selected?.compraNoFechamentoNaFaturaQueFecha ?? false
  const fechamentoMudou = !!selected && credito && cardForm.compraNoFechamento !== fechamentoAtual
  const diasMudaram = !!selected && (Number(cardForm.diaFechamento) !== selected.diaFechamento || Number(cardForm.diaVencimento) !== selected.diaVencimento)

  const editarCartao = useMutation({
    mutationFn: () => cartoesApi.atualizar(selected!.id, credito
      ? {
        nome: cardForm.nome.trim(), limite: Number(cardForm.limite),
        diaFechamento: Number(cardForm.diaFechamento), diaVencimento: Number(cardForm.diaVencimento), temDebito: cardForm.temDebito,
        // Só manda se mudou (ausente = não muda); mudar NÃO move compras já lançadas.
        ...(fechamentoMudou ? { compraNoFechamentoNaFaturaQueFecha: cardForm.compraNoFechamento } : {}),
      }
      : { nome: cardForm.nome.trim() }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fatura'] })
      qc.invalidateQueries({ queryKey: ['cartoes'] })
      qc.invalidateQueries({ queryKey: ['faturas-resumo'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
      setEditandoCartao(false)
      qc.invalidateQueries({ queryKey: ['contas'] })
      toast.success(credito && diasMudaram ? 'Cartão atualizado. As faturas em aberto foram recalculadas.' : 'Cartão atualizado.')
    },
    onError: (e: Error) => setErro(e.message),
  })

  function openEditCartao() {
    if (!selected) return
    setCardForm({ modalidade: selected.modalidade, nome: selected.nome, limite: String(selected.limite), diaFechamento: String(selected.diaFechamento), diaVencimento: String(selected.diaVencimento), accountId: selected.accountId, saldoInicial: '', temDebito: selected.temDebito, compraNoFechamento: selected.compraNoFechamentoNaFaturaQueFecha ?? false })
    setErro(null)
    setEditandoCartao(true)
  }

  function submitEdicao() {
    const dia = (v: string) => Number.isInteger(Number(v)) && Number(v) >= 1 && Number(v) <= 28
    if (!credito) {
      if (!cardForm.nome.trim()) { setErro('Informe o nome do cartão.'); return }
      editarCartao.mutate()
      return
    }
    if (!cardForm.nome.trim() || !cardForm.limite) { setErro('Preencha nome e limite.'); return }
    if (!dia(cardForm.diaFechamento) || !dia(cardForm.diaVencimento)) { setErro('Os dias de fechamento e vencimento vão de 1 a 28.'); return }
    editarCartao.mutate()
  }

  function openCardModal() {
    setCardForm(EMPTY_CARD)
    setErro(null)
    setCardModal(true)
  }

  function openCompraModal(debito = false) {
    setCompra({ ...emptyCompra(), debito })
    setErro(null)
    setMostrarNovaCategoria(false)
    setNovaCategoria('')
    setCompraModal(true)
  }

  function submitCard() {
    const dia = (v: string) => Number.isInteger(Number(v)) && Number(v) >= 1 && Number(v) <= 28
    if (!cardForm.nome.trim()) { setErro('Informe o nome do cartão.'); return }
    if (cardForm.modalidade === ModalidadeCartao.Debito && !cardForm.accountId) { setErro('Escolha a conta de onde as compras saem.'); return }
    if (cardForm.modalidade === ModalidadeCartao.Credito) {
      if (!cardForm.accountId || !cardForm.limite) { setErro('Preencha nome, conta e limite.'); return }
      if (!dia(cardForm.diaFechamento) || !dia(cardForm.diaVencimento)) { setErro('Os dias de corte e de pagamento vão de 1 a 28.'); return }
    }
    setErro(null)
    criarCartao.mutate()
  }

  function submitCompra() {
    if (!compra.description.trim() || !compra.amount) {
      setErro('Preencha descrição e valor.')
      return
    }
    adicionarCompra.mutate()
  }

  return (
    <>
      <PageHeader title="Cartões" />

      {isLoading ? (
        <LoadingState />
      ) : cartoes.length === 0 ? (
        <Card>
          <EmptyState icon="ri-bank-card-line" message="Nenhum cartão cadastrado." action={<Button data-tutorial="cartao.novo" icon="ri-add-line" onClick={openCardModal}>Novo cartão</Button>} />
        </Card>
      ) : (
        <div className="grid gap-6 xl:grid-cols-[320px_1fr]">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h5 data-tutorial="csv.cartao" className="text-15 font-medium text-white">Meus cartões</h5>
              <Button data-tutorial="cartao.novo" size="sm" variant="soft-primary" icon="ri-add-line" onClick={openCardModal}>Novo</Button>
            </div>
            {cartoes.map((c) => {
              const pct = c.limite > 0 ? (c.saldoUtilizado / c.limite) * 100 : 0
              const active = selected?.id === c.id
              const ehCredito = c.modalidade === ModalidadeCartao.Credito
              const ehPrePago = c.modalidade === ModalidadeCartao.PrePago
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => { setSelectedId(c.id); setAba('fatura') }}
                  aria-pressed={active}
                  className={cn('block w-full rounded bg-surface p-4 text-left shadow-card transition-colors', active ? 'ring-1 ring-primary' : 'hover:bg-surface2/60')}
                >
                  <div className="flex items-center gap-3">
                    <AvatarIcon icon="ri-bank-card-2-line" tone={active ? 'primary' : 'muted'} />
                    <div className="min-w-0 flex-1">
                      <p className="flex items-center gap-2 font-medium text-white">
                        <span className="truncate">{c.nome}</span>
                        <Badge variant={ehCredito ? 'info' : ehPrePago ? 'primary' : 'muted'} className="normal-case">{MODALIDADE_CARTAO_LABEL[c.modalidade]}</Badge>
                        <DonoBadge nome={c.ownerNome} />
                      </p>
                      <p className="truncate text-2xs text-muted">{ehPrePago ? 'Saldo próprio do cartão' : c.accountName}</p>
                    </div>
                  </div>
                  {ehCredito ? (
                    <>
                      <div className="mt-3 flex items-end justify-between">
                        <div>
                          <p className="text-2xs uppercase text-muted">Disponível</p>
                          <p className="text-base font-semibold text-white">{formatMoney(c.saldoDisponivel)}</p>
                        </div>
                        <p className="text-2xs text-muted">de {formatMoney(c.limite)}</p>
                      </div>
                      <ProgressBar className="mt-2" value={pct} tone={pct > 80 ? 'expense' : pct > 60 ? 'warning' : 'income'} />
                      <p className="mt-2 flex justify-between text-2xs text-muted">
                        <span><i className="ri-calendar-close-line" aria-hidden /> Fecha dia {c.diaFechamento}</span>
                        <span><i className="ri-calendar-check-line" aria-hidden /> Vence dia {c.diaVencimento}</span>
                      </p>
                      {c.temDebito && (
                        <p className="mt-1 flex justify-between text-2xs text-muted">
                          <span><i className="ri-bank-card-line" aria-hidden /> Também é débito</span>
                          <span>Débito no mês {formatMoney(c.gastoMes)}</span>
                        </p>
                      )}
                    </>
                  ) : (
                    <div className="mt-3 flex items-end justify-between">
                      <div>
                        <p className="text-2xs uppercase text-muted">{ehPrePago ? 'Saldo' : 'Gasto no mês'}</p>
                        <p className={cn('text-base font-semibold tabular-nums', ehPrePago && (c.saldo ?? 0) < 0 ? 'text-expense' : 'text-white')}>
                          {formatMoney(ehPrePago ? (c.saldo ?? 0) : c.gastoMes)}
                        </p>
                      </div>
                      {ehPrePago && <p className="text-2xs text-muted">gasto no mês {formatMoney(c.gastoMes)}</p>}
                    </div>
                  )}
                </button>
              )
            })}
          </div>

          <div className="min-w-0 space-y-4">
          {comDebito && (
            <div className="flex gap-1 border-b border-border/70" role="tablist" aria-label="Fatura ou débito">
              {([['fatura', 'Fatura (crédito)'], ['debito', 'Débito']] as const).map(([k, rotulo]) => (
                <button
                  key={k} type="button" role="tab" aria-selected={abaAtiva === k}
                  onClick={() => setAba(k)}
                  className={cn('-mb-px border-b-2 px-4 py-2 text-13 font-medium transition-colors', abaAtiva === k ? 'border-primary text-primary' : 'border-transparent text-muted hover:text-white')}
                >{rotulo}</button>
              ))}
            </div>
          )}

          {selected && (!credito || abaAtiva === 'debito') && (
            <div>
              <PainelContaCartao
                cartao={selected} mes={mes} ano={ano}
                onMudarMes={(m, a) => { setMes(m); setAno(a) }}
                onLancarCompra={() => openCompraModal(true)} onEditar={openEditCartao}
              />
            </div>
          )}

          {selected && credito && abaAtiva === 'fatura' && (
            <Card>
              <CardHeader
                title={<>Fatura · {selected.nome}</>}
                actions={
                  <>
                    <MonthNavigator mes={mes} ano={ano} onChange={(m, a) => { setMes(m); setAno(a) }} />
                    <Button variant="light" icon="ri-pencil-line" onClick={openEditCartao}>Editar cartão</Button>
                    {fatura && (
                      <>
                        <BotaoAjusteFaturaAtalho
                          cartaoId={selected.id} faturaId={fatura.id} accountId={selected.accountId}
                          onAjustado={() => qc.invalidateQueries()}
                        />
                        <BotaoImportarCsv cartaoId={selected.id} faturaIdPadrao={fatura.id} onImportado={invalidarFatura} />
                      </>
                    )}
                    <Button icon="ri-add-line" onClick={() => openCompraModal(false)}>Lançar compra</Button>
                  </>
                }
              />
              {loadingFatura ? (
                <LoadingState />
              ) : !fatura ? (
                <EmptyState icon="ri-file-list-3-line" message="Nenhuma fatura neste mês." />
              ) : (
                <>
                  <div className="grid gap-px border-b border-border/60 bg-border/60 sm:grid-cols-3">
                    <div className="bg-surface p-4">
                      <p className="text-2xs uppercase text-muted">Total da fatura</p>
                      <p className="text-xl font-semibold text-white">{formatMoney(fatura.valorTotal)}</p>
                    </div>
                    <div className="bg-surface p-4">
                      <p className="text-2xs uppercase text-muted">Vencimento</p>
                      <p className="text-xl font-semibold text-white">{formatDate(fatura.dataVencimento)}</p>
                    </div>
                    <div className="flex items-center justify-between gap-2 bg-surface p-4">
                      <div>
                        <p className="text-2xs uppercase text-muted">Status</p>
                        <Badge variant={STATUS_VARIANT[fatura.status] ?? 'muted'} className="mt-1">{STATUS_FATURA_LABEL[fatura.status] ?? fatura.status}</Badge>
                        {fatura.status === StatusFatura.Paga && fatura.pagaSemDebito && <Badge variant="muted" className="ml-1 mt-1">Paga sem débito</Badge>}
                      </div>
                      {fatura.status !== StatusFatura.Paga && fatura.valorTotal > 0 && (
                        <Button size="sm" variant="soft-success" icon="ri-check-double-line" onClick={abrirPagamento}>Pagar</Button>
                      )}
                      {fatura.status === StatusFatura.Paga && (
                        <Button size="sm" variant="light" icon="ri-lock-unlock-line" onClick={() => setReabrindo(true)}>Reabrir</Button>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-1 border-b border-border/70 px-4" role="tablist" aria-label="Compras ou categorias da fatura">
                    {([['compras', 'Compras'], ['categorias', 'Por categoria']] as const).map(([k, rotulo]) => (
                      <button
                        key={k} type="button" role="tab" aria-selected={visaoFatura === k}
                        onClick={() => setVisaoFatura(k)}
                        className={cn('-mb-px border-b-2 px-4 py-2 text-13 font-medium transition-colors', visaoFatura === k ? 'border-primary text-primary' : 'border-transparent text-muted hover:text-white')}
                      >{rotulo}</button>
                    ))}
                  </div>

                  {visaoFatura === 'categorias' ? (
                    <CardBody><FaturaCategoriasPanel faturaId={fatura.id} /></CardBody>
                  ) : fatura.transacoes.length === 0 ? (
                    <CardBody><EmptyState icon="ri-shopping-bag-3-line" message="Nenhuma compra nesta fatura." /></CardBody>
                  ) : (
                    <>
                    <div className="border-b border-border/60 p-4">
                      <div className="w-64">
                        <Input placeholder="Buscar por nome" value={ordenacaoFatura.busca} onChange={(e) => ordenacaoFatura.setBusca(e.target.value)} />
                      </div>
                    </div>
                    <Table>
                      <THead>
                        <tr>
                          <ThOrdenavel campo="nome" ativo={ordenacaoFatura.ordenarPor} desc={ordenacaoFatura.ordemDesc} onClick={ordenacaoFatura.alternarOrdenacao}>Descrição</ThOrdenavel>
                          <ThOrdenavel campo="data" ativo={ordenacaoFatura.ordenarPor} desc={ordenacaoFatura.ordemDesc} onClick={ordenacaoFatura.alternarOrdenacao}>Data</ThOrdenavel>
                          <ThOrdenavel campo="parcela" ativo={ordenacaoFatura.ordenarPor} desc={ordenacaoFatura.ordemDesc} onClick={ordenacaoFatura.alternarOrdenacao}>Parcela</ThOrdenavel>
                          <ThOrdenavel campo="valor" ativo={ordenacaoFatura.ordenarPor} desc={ordenacaoFatura.ordemDesc} onClick={ordenacaoFatura.alternarOrdenacao} className="text-right">Valor</ThOrdenavel>
                          <Th className="text-right">Ações</Th>
                        </tr>
                      </THead>
                      <tbody>
                        {ordenacaoFatura.itens.map((t) => (
                          <Tr key={t.id}>
                            <Td>
                              <p className="flex items-center gap-1.5 font-medium text-white">
                                {t.description}
                                {t.ehAjusteFatura && <Badge variant="info">Ajuste</Badge>}
                                {t.importadoCsv && <Badge variant="muted">CSV</Badge>}
                              </p>
                              {t.categoriaNome && <p className="text-2xs text-muted">{t.categoriaNome}</p>}
                            </Td>
                            <Td className="whitespace-nowrap text-muted">{formatDate(t.date)}</Td>
                            <Td>{t.parcelaNumero ? <Badge variant="info">{t.parcelaNumero}/{t.parcelaTotalNumero}</Badge> : <span className="text-muted">À vista</span>}</Td>
                            <Td className={cn('whitespace-nowrap text-right font-semibold', t.ehAjusteFatura ? 'text-income' : 'text-expense')}>
                              {t.ehAjusteFatura ? '− ' : ''}{formatMoney(t.amount)}
                            </Td>
                            <Td className="text-right">
                              {t.ehAjusteFatura && (
                                <AjusteFaturaAcoes ajuste={t} faturaPaga={fatura.status === StatusFatura.Paga} totalFatura={fatura.valorTotal} />
                              )}
                              {!t.ehAjusteFatura && (
                                <div className="flex justify-end gap-1">
                                  <IconButton icon="ri-pencil-fill" label={`Editar ${t.description}`} tone="info" onClick={() => abrirEdicao(t)} />
                                  <IconButton icon="ri-arrow-left-right-line" label={`Mover ${t.description} pra outra fatura`} tone="muted" onClick={() => setMovendo(t)} />
                                  <IconButton icon="ri-delete-bin-5-fill" label={`Excluir ${t.description}`} tone="danger" onClick={() => setParaExcluir(t)} />
                                </div>
                              )}
                            </Td>
                          </Tr>
                        ))}
                      </tbody>
                    </Table>
                    </>
                  )}
                </>
              )}
            </Card>
          )}
          </div>
        </div>
      )}

      <Modal
        open={cardModal}
        onClose={() => setCardModal(false)}
        title="Novo cartão"
        footer={
          <>
            <Button variant="light" onClick={() => setCardModal(false)}>Cancelar</Button>
            <Button icon="ri-add-line" onClick={submitCard} loading={criarCartao.isPending}>Criar cartão</Button>
          </>
        }
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <p className="mb-1.5 text-13 font-medium text-white">Tipo do cartão</p>
            <div className="grid gap-2 sm:grid-cols-3" role="group" aria-label="Tipo do cartão">
              {MODALIDADES.map((m) => (
                <button
                  key={m.valor}
                  type="button"
                  aria-pressed={cardForm.modalidade === m.valor}
                  onClick={() => { setCardForm((f) => ({ ...f, modalidade: m.valor })); setErro(null) }}
                  className={cn('rounded border px-3 py-2 text-left transition-colors', cardForm.modalidade === m.valor ? 'border-primary bg-primary/10 text-primary' : 'border-border/70 text-muted hover:bg-surface2')}
                >
                  <span className="block text-13 font-semibold">{m.titulo}</span>
                </button>
              ))}
            </div>
            <p className="mt-1.5 text-13 text-muted">{MODALIDADES.find((m) => m.valor === cardForm.modalidade)?.ajuda}</p>
          </div>
          <div className="sm:col-span-2">
            <Input label="Nome do cartão" value={cardForm.nome} onChange={(e) => setCardForm((f) => ({ ...f, nome: e.target.value }))} placeholder={cardForm.modalidade === ModalidadeCartao.PrePago ? 'Ex: Vale-alimentação' : 'Ex: Nubank'} />
          </div>
          {cardForm.modalidade !== ModalidadeCartao.PrePago && (
            <Select label={cardForm.modalidade === ModalidadeCartao.Debito ? 'Conta de onde a compra sai' : 'Conta de pagamento'} value={cardForm.accountId} onChange={(e) => setCardForm((f) => ({ ...f, accountId: e.target.value }))}>
              <option value="">Selecione...</option>
              {contas.map((c) => <option key={c.id} value={c.id}>{c.name} · {c.institution}</option>)}
            </Select>
          )}
          {cardForm.modalidade === ModalidadeCartao.Credito && (
            <>
              <Input label="Limite (R$)" type="number" min={0} step="0.01" value={cardForm.limite} onChange={(e) => setCardForm((f) => ({ ...f, limite: e.target.value }))} />
              <Input label="Dia de corte" type="number" min={1} max={28} value={cardForm.diaFechamento} onChange={(e) => setCardForm((f) => ({ ...f, diaFechamento: e.target.value }))} />
              <Input label="Dia de pagamento" type="number" min={1} max={28} value={cardForm.diaVencimento} onChange={(e) => setCardForm((f) => ({ ...f, diaVencimento: e.target.value }))} />
              <CaixaDebito marcado={cardForm.temDebito} onChange={(v) => setCardForm((f) => ({ ...f, temDebito: v }))} />
              <SeletorFechamento valor={cardForm.compraNoFechamento} onChange={(v) => setCardForm((f) => ({ ...f, compraNoFechamento: v }))} />
            </>
          )}
          {cardForm.modalidade === ModalidadeCartao.PrePago && (
            <>
              <Input label="Saldo atual (R$)" type="number" step="0.01" value={cardForm.saldoInicial} onChange={(e) => setCardForm((f) => ({ ...f, saldoInicial: e.target.value }))} placeholder="0,00" />
              <p className="self-end text-13 text-muted">O app cria uma conta "Benefício" com esse nome para guardar o saldo. Ela soma no seu patrimônio.</p>
            </>
          )}
          {erro && <p className="flex items-center gap-1.5 rounded bg-expense/10 px-3 py-2 text-13 text-expense sm:col-span-2" role="alert"><i className="ri-error-warning-line" aria-hidden /> {erro}</p>}
        </div>
      </Modal>

      <Modal
        open={editandoCartao}
        onClose={() => setEditandoCartao(false)}
        title={`Editar cartão · ${selected?.nome ?? ''}`}
        footer={
          <>
            <Button variant="light" onClick={() => setEditandoCartao(false)}>Cancelar</Button>
            <Button icon="ri-save-line" onClick={submitEdicao} loading={editarCartao.isPending}>Salvar</Button>
          </>
        }
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Input label="Nome do cartão" value={cardForm.nome} onChange={(e) => setCardForm((f) => ({ ...f, nome: e.target.value }))} />
          </div>
          {credito ? (
            <>
              <Input label="Limite (R$)" type="number" min={0} step="0.01" value={cardForm.limite} onChange={(e) => setCardForm((f) => ({ ...f, limite: e.target.value }))} />
              <span />
              <Input label="Dia de corte (fechamento)" type="number" min={1} max={28} value={cardForm.diaFechamento} onChange={(e) => setCardForm((f) => ({ ...f, diaFechamento: e.target.value }))} />
              <Input label="Dia de pagamento (vencimento)" type="number" min={1} max={28} value={cardForm.diaVencimento} onChange={(e) => setCardForm((f) => ({ ...f, diaVencimento: e.target.value }))} />
              <CaixaDebito marcado={cardForm.temDebito} onChange={(v) => setCardForm((f) => ({ ...f, temDebito: v }))} />
              <SeletorFechamento valor={cardForm.compraNoFechamento} onChange={(v) => setCardForm((f) => ({ ...f, compraNoFechamento: v }))} />
              {fechamentoMudou && selected && <PreviaFechamento cartaoId={selected.id} novoValor={cardForm.compraNoFechamento} />}
              <p className="text-13 text-muted sm:col-span-2">Ao mudar os dias e salvar, as compras das faturas em aberto são reorganizadas pelas novas datas. Faturas já pagas não mudam.</p>
            </>
          ) : (
            <p className="text-13 text-muted sm:col-span-2">Débito e pré-pago não têm limite nem fatura. Aqui só dá para mudar o nome{selected?.modalidade === ModalidadeCartao.PrePago ? ' (a conta Benefício muda junto)' : ''}.</p>
          )}
          {erro && <p className="flex items-center gap-1.5 rounded bg-expense/10 px-3 py-2 text-13 text-expense sm:col-span-2" role="alert"><i className="ri-error-warning-line" aria-hidden /> {erro}</p>}
        </div>
      </Modal>

      <Modal
        open={compraModal}
        onClose={() => setCompraModal(false)}
        title={`Lançar compra · ${selected?.nome ?? ''}`}
        footer={
          <>
            <Button variant="light" onClick={() => setCompraModal(false)}>Cancelar</Button>
            <Button icon="ri-add-line" onClick={submitCompra} loading={adicionarCompra.isPending}>Lançar</Button>
          </>
        }
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Input label="Descrição" value={compra.description} onChange={(e) => setCompra((f) => ({ ...f, description: e.target.value }))} placeholder="Ex: Notebook" />
          </div>
          {comDebito && (
            <div className="sm:col-span-2">
              <Select label="Como pagou" value={compra.debito ? 'debito' : 'credito'} onChange={(e) => setCompra((f) => ({ ...f, debito: e.target.value === 'debito' }))}>
                <option value="credito">Crédito (entra na fatura)</option>
                <option value="debito">Débito (sai da conta na hora)</option>
              </Select>
            </div>
          )}
          <Input label={!compraAVista ? 'Valor total (R$)' : 'Valor (R$)'} type="number" min={0} step="0.01" value={compra.amount} onChange={(e) => setCompra((f) => ({ ...f, amount: e.target.value }))} />
          {!compraAVista ? (
            <Input label="Parcelas" type="number" min={1} max={48} value={compra.numParcelas} onChange={(e) => setCompra((f) => ({ ...f, numParcelas: e.target.value }))} />
          ) : (
            <p className="self-end pb-2 text-13 text-muted">À vista: sai {selected?.modalidade === ModalidadeCartao.PrePago ? 'do saldo do cartão' : `da conta ${selected?.accountName}`} na hora.</p>
          )}
          <Input label="Data da compra" type="date" value={compra.date} onChange={(e) => setCompra((f) => ({ ...f, date: e.target.value }))} />
          <div>
            <Select label="Categoria" value={compra.categoriaId} onChange={(e) => setCompra((f) => ({ ...f, categoriaId: e.target.value }))}>
              <option value="">Sem categoria</option>
              {categorias.map((c) => <option key={c.id} value={c.id}>{c.icone} {c.nome}</option>)}
            </Select>
            {!mostrarNovaCategoria ? (
              <button type="button" className="mt-1.5 text-xs text-primary hover:underline" onClick={() => setMostrarNovaCategoria(true)}>
                <i className="ri-add-line" aria-hidden /> Cadastrar nova categoria
              </button>
            ) : (
              <div className="mt-2 space-y-2">
                <Input value={novaCategoria} onChange={(e) => setNovaCategoria(e.target.value)} placeholder="Nome da categoria" autoFocus />
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => novaCategoria.trim() && criarCategoria.mutate()} loading={criarCategoria.isPending}>Criar</Button>
                  <Button size="sm" variant="ghost" onClick={() => setMostrarNovaCategoria(false)}>Cancelar</Button>
                </div>
              </div>
            )}
          </div>
          <div className="sm:col-span-2">
            <Input label="Observação" value={compra.notes} onChange={(e) => setCompra((f) => ({ ...f, notes: e.target.value }))} placeholder="Opcional" />
          </div>
          {!compraAVista && Number(compra.numParcelas) > 1 && Number(compra.amount) > 0 && (
            <p className="text-13 text-muted sm:col-span-2">
              {compra.numParcelas}x de <strong className="text-white">{formatMoney(Number(compra.amount) / Number(compra.numParcelas))}</strong> nas próximas faturas.
            </p>
          )}
          {erro && <p className="flex items-center gap-1.5 rounded bg-expense/10 px-3 py-2 text-13 text-expense sm:col-span-2" role="alert"><i className="ri-error-warning-line" aria-hidden /> {erro}</p>}
        </div>
      </Modal>

      <Modal
        open={confirmPagar}
        onClose={() => setConfirmPagar(false)}
        title="Pagar fatura"
        footer={
          <>
            <Button variant="light" onClick={() => setConfirmPagar(false)}>Cancelar</Button>
            <Button icon="ri-check-double-line" loading={pagarFatura.isPending} disabled={!dataPagamento} onClick={() => fatura && pagarFatura.mutate({
              faturaId: fatura.id, data: dataPagamento, semDebito: vinculando ? false : semDebito,
              vincularTransacaoId: vinculando ? pagamentoImportado!.transacaoId : undefined,
            })}>Confirmar pagamento</Button>
          </>
        }
      >
        <p className="mb-3 text-13 text-muted">
          Fatura de <strong className="text-white">{formatMoney(fatura?.valorTotal ?? 0)}</strong> do cartão {selected?.nome}. {vinculando || semDebito ? 'O saldo da conta não será alterado.' : 'O valor sai da conta vinculada na data escolhida.'}
        </p>
        <Input label="Data do pagamento" type="date" value={dataPagamento} onChange={(e) => setDataPagamento(e.target.value)} />
        {pagamentoImportado && (
          <div className="mt-3 space-y-2 rounded-lg border border-info/40 bg-info/5 p-3 text-13">
            <p className="text-white">
              <i className="ri-information-line text-info" aria-hidden /> Este pagamento já veio da Pluggy: <strong>{formatMoney(pagamentoImportado.valor)}</strong> em {formatDate(pagamentoImportado.data)} ({pagamentoImportado.descricao})
            </p>
            <label className="flex items-start gap-2 text-white">
              <input type="radio" name="pagamento-importado" className="mt-0.5" checked={vincularImportado} onChange={() => setVincularImportado(true)} />
              <span>Vincular a este pagamento (paga sem débito)<span className="block text-12 text-muted">A fatura fica paga sem debitar a conta de novo, e o débito deixa de contar como gasto.</span></span>
            </label>
            <label className="flex items-start gap-2 text-white">
              <input type="radio" name="pagamento-importado" className="mt-0.5" checked={!vincularImportado} onChange={() => setVincularImportado(false)} />
              <span>Pagar normalmente<span className="block text-12 text-muted">Debita a conta de novo. Só faça isso se for outro pagamento.</span></span>
            </label>
          </div>
        )}
        <label className={cn('mt-3 flex items-start gap-2 text-13 text-white', vinculando && 'opacity-50')}>
          <input type="checkbox" className="mt-0.5" disabled={vinculando} checked={semDebito} onChange={(e) => setSemDebito(e.target.checked)} />
          <span>
            Só marcar como paga (não debitar da conta)
            <span className="block text-12 text-muted">Use quando você já ajustou o saldo da conta. O saldo não será alterado.</span>
          </span>
        </label>
        {fatura && dataPagamento && (() => {
          const venc = fatura.dataVencimento.slice(0, 10)
          if (dataPagamento < venc) return <p className="mt-2 text-13 text-info"><i className="ri-time-line" aria-hidden /> Pagamento adiantado em relação ao vencimento ({formatDate(fatura.dataVencimento)}).</p>
          if (dataPagamento > venc) return <p className="mt-2 text-13 text-expense"><i className="ri-alarm-warning-line" aria-hidden /> Pagamento depois do vencimento ({formatDate(fatura.dataVencimento)}).</p>
          return <p className="mt-2 text-13 text-muted">Pagamento no vencimento.</p>
        })()}
      </Modal>

      <Modal
        open={!!editando}
        onClose={() => setEditando(null)}
        title="Editar lançamento"
        footer={
          <>
            <Button variant="light" onClick={() => setEditando(null)}>Cancelar</Button>
            <Button icon="ri-save-line" loading={salvarEdicao.isPending} onClick={confirmarEdicao}>Salvar alterações</Button>
          </>
        }
      >
        <div className="grid gap-4 sm:grid-cols-2">
          {editando?.faturaPaga && <p className="text-13 text-warning sm:col-span-2">Fatura paga: só a categoria pode ser alterada.</p>}
          <Input label="Descrição" className="sm:col-span-2" disabled={!!editando?.faturaPaga && !editando.ehDaPluggy} value={editForm.description} onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))} />
          <Input label="Valor (R$)" type="number" min={0} step="0.01" disabled={!!editando?.faturaPaga} value={editForm.amount} onChange={(e) => setEditForm((f) => ({ ...f, amount: e.target.value }))} />
          <Input label="Data" type="date" disabled={!!editando?.faturaPaga} value={editForm.date} onChange={(e) => setEditForm((f) => ({ ...f, date: e.target.value }))} />
          <Select label="Categoria" className="sm:col-span-2" value={editForm.categoriaId} onChange={(e) => setEditForm((f) => ({ ...f, categoriaId: e.target.value }))}>
            <option value="">Sem categoria</option>
            {categorias.map((c) => <option key={c.id} value={c.id}>{c.icone} {c.nome}</option>)}
          </Select>
          <Input label="Observação" className="sm:col-span-2" disabled={!!editando?.faturaPaga && !editando.ehDaPluggy} value={editForm.notes} onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Opcional" />
          {editando?.ehDaPluggy && (
            <div className="sm:col-span-2">
              <BotaoFundirLancamento transacaoId={editando.id} onFundido={() => { invalidarFatura(); setEditando(null) }} />
            </div>
          )}
          {erroEdicao && <p className="flex items-center gap-1.5 rounded bg-expense/10 px-3 py-2 text-13 text-expense sm:col-span-2" role="alert"><i className="ri-error-warning-line" aria-hidden /> {erroEdicao}</p>}
        </div>
      </Modal>

      <ConfirmDialog
        open={!!paraExcluir && !((paraExcluir.parcelaTotalNumero ?? 0) > 1)}
        onClose={() => setParaExcluir(null)}
        onConfirm={() => paraExcluir && remover.mutate({ id: paraExcluir.id, escopo: EscopoParcelas.Apenas })}
        loading={remover.isPending}
        message={<>O lançamento <strong className="text-white">{paraExcluir?.description}</strong> será excluído permanentemente.</>}
      />

      <ConfirmDialog
        open={reabrindo}
        onClose={() => setReabrindo(false)}
        onConfirm={() => reabrir.mutate()}
        loading={reabrir.isPending}
        confirmLabel="Sim, reabrir"
        title="Reabrir fatura?"
        message={<>
          {fatura?.pagaSemDebito
            ? <>Esta fatura foi paga sem débito: o saldo da conta <strong className="text-white">não será alterado</strong> ao reabrir. </>
            : <><strong className="text-white">{formatMoney(fatura?.valorTotal ?? 0)}</strong> vão voltar pro saldo da conta. </>}
          A fatura fica "Fechada" até você pagar de novo — nesse meio tempo dá pra editar, excluir ou mover os lançamentos dela.
        </>}
      />

      <Modal
        open={!!movendo}
        onClose={() => { setMovendo(null); setFaturaDestinoId('') }}
        title="Mover pra outra fatura"
        footer={
          <>
            <Button variant="light" onClick={() => { setMovendo(null); setFaturaDestinoId('') }}>Cancelar</Button>
            <Button icon="ri-arrow-left-right-line" disabled={!faturaDestinoId} loading={mover.isPending} onClick={() => mover.mutate()}>Mover</Button>
          </>
        }
      >
        <p className="mb-3 text-13 text-muted">
          Move <strong className="text-white">{movendo?.description}</strong> pra outra fatura do mesmo cartão — funciona mesmo se a fatura de
          origem ou a de destino já estiverem fechadas ou pagas (o saldo da conta e o total de cada fatura se ajustam sozinhos).
        </p>
        <Select label="Fatura de destino" value={faturaDestinoId} onChange={(e) => setFaturaDestinoId(e.target.value)}>
          <option value="">Selecione...</option>
          {faturasDoCartao.filter((f) => f.id !== fatura?.id).map((f) => (
            <option key={f.id} value={f.id}>{NOMES_MES[f.mes - 1]} de {f.ano} · {STATUS_FATURA_LABEL[f.status]}</option>
          ))}
        </Select>
      </Modal>

      <Modal
        open={escopoSalvar || (!!paraExcluir && (paraExcluir.parcelaTotalNumero ?? 0) > 1)}
        onClose={() => { setEscopoSalvar(false); setParaExcluir(null) }}
        title={escopoSalvar ? 'Salvar alterações em qual parcela?' : 'Excluir qual parcela?'}
        footer={
          <>
            <Button variant="light" onClick={() => { setEscopoSalvar(false); setParaExcluir(null) }}>Cancelar</Button>
            {escopoSalvar ? (
              <Button icon="ri-save-line" loading={salvarEdicao.isPending} onClick={() => salvarEdicao.mutate(escopoEscolhido)}>Salvar</Button>
            ) : (
              <Button variant="danger" icon="ri-delete-bin-line" loading={remover.isPending} onClick={() => paraExcluir && remover.mutate({ id: paraExcluir.id, escopo: escopoEscolhido })}>Excluir</Button>
            )}
          </>
        }
      >
        <p className="mb-3 text-13 text-muted">Esta compra é parcelada. Escolha o que a ação atinge:</p>
        <div className="space-y-2">
          {ESCOPOS_FATURA.map((o) => (
            <label key={o.valor} className={cn('flex cursor-pointer items-start gap-3 rounded border px-3 py-2', escopoEscolhido === o.valor ? 'border-primary bg-primary/10' : 'border-border/70')}>
              <input type="radio" name="escopo-fatura" className="mt-1 accent-primary" checked={escopoEscolhido === o.valor} onChange={() => setEscopoEscolhido(o.valor)} />
              <span><span className="block text-white">{o.rotulo}</span><span className="text-2xs text-muted">{o.ajuda}</span></span>
            </label>
          ))}
        </div>
      </Modal>
    </>
  )
}
